import React, { useState, useCallback, useRef } from 'react';
import { FileType, Image, Video, FileAudio, File, Download, Trash2, Settings, Loader2, CheckCircle, AlertCircle, Youtube, Music, Search, Clock, Eye, Globe, BarChart3, Link2, ImageIcon, FileText, Smartphone, Zap, XCircle, Info, ChevronDown, ChevronUp, Monitor, Tablet, Laptop, Frame } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import AnimatedText from '../components/AnimatedText';
import FloatingCard from '../components/FloatingCard';
import ShimmerButton from '../components/ShimmerButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Tools = () => {
  const [files, setFiles] = useState([]);
  const [converting, setConverting] = useState({});
  const [converted, setConverted] = useState({});
  const [activeTab, setActiveTab] = useState('convert');
  const [compressionQuality, setCompressionQuality] = useState(80);
  
  // YouTube states
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [youtubeInfo, setYoutubeInfo] = useState(null);
  const [youtubeError, setYoutubeError] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');

  // SEO states
  const [seoUrl, setSeoUrl] = useState('');
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoResult, setSeoResult] = useState(null);
  const [seoError, setSeoError] = useState('');
  const [showAllIssues, setShowAllIssues] = useState(false);

  // Mockup states
  const [mockupImage, setMockupImage] = useState(null);
  const [mockupPreview, setMockupPreview] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState('iphone');
  const [mockupBackground, setMockupBackground] = useState('#f3f4f6');
  const [generatingMockup, setGeneratingMockup] = useState(false);
  const [generatedMockup, setGeneratedMockup] = useState(null);
  const mockupCanvasRef = useRef(null);

  const imageFormats = ['PNG', 'JPG', 'WEBP', 'GIF', 'BMP'];
  const videoFormats = ['MP4', 'WEBM'];
  const audioFormats = ['MP3', 'WAV', 'OGG'];

  // Device configurations for mockups
  const deviceConfigs = {
    iphone: {
      name: 'iPhone 15 Pro',
      width: 430,
      height: 932,
      screenX: 20,
      screenY: 20,
      screenWidth: 390,
      screenHeight: 844,
      borderRadius: 55,
      frameColor: '#1a1a1a',
      notch: true,
      category: 'devices'
    },
    macbook: {
      name: 'MacBook Pro',
      width: 1200,
      height: 750,
      screenX: 100,
      screenY: 40,
      screenWidth: 1000,
      screenHeight: 625,
      borderRadius: 12,
      frameColor: '#2d2d2d',
      notch: false,
      category: 'devices'
    },
    ipad: {
      name: 'iPad Pro',
      width: 820,
      height: 1180,
      screenX: 30,
      screenY: 50,
      screenWidth: 760,
      screenHeight: 1080,
      borderRadius: 35,
      frameColor: '#1a1a1a',
      notch: false,
      category: 'devices'
    },
    android: {
      name: 'Android Phone',
      width: 412,
      height: 915,
      screenX: 16,
      screenY: 40,
      screenWidth: 380,
      screenHeight: 835,
      borderRadius: 40,
      frameColor: '#0a0a0a',
      notch: false,
      category: 'devices'
    },
    browser: {
      name: 'Browser Window',
      width: 1200,
      height: 800,
      screenX: 0,
      screenY: 80,
      screenWidth: 1200,
      screenHeight: 720,
      borderRadius: 12,
      frameColor: '#ffffff',
      notch: false,
      category: 'devices'
    },
    // Print mockups
    businesscard: {
      name: 'Carte de Visite',
      width: 450,
      height: 270,
      screenX: 15,
      screenY: 15,
      screenWidth: 420,
      screenHeight: 240,
      borderRadius: 10,
      frameColor: '#ffffff',
      notch: false,
      category: 'print',
      shadow: true
    },
    flyer: {
      name: 'Flyer A5',
      width: 500,
      height: 700,
      screenX: 0,
      screenY: 0,
      screenWidth: 500,
      screenHeight: 700,
      borderRadius: 0,
      frameColor: '#ffffff',
      notch: false,
      category: 'print',
      shadow: true
    },
    poster: {
      name: 'Affiche A3',
      width: 600,
      height: 850,
      screenX: 0,
      screenY: 0,
      screenWidth: 600,
      screenHeight: 850,
      borderRadius: 0,
      frameColor: '#ffffff',
      notch: false,
      category: 'print',
      shadow: true
    },
    busstop: {
      name: 'Abribus',
      width: 800,
      height: 600,
      screenX: 80,
      screenY: 60,
      screenWidth: 280,
      screenHeight: 420,
      borderRadius: 5,
      frameColor: '#3a3a3a',
      notch: false,
      category: 'print',
      isBusStop: true
    },
    billboard: {
      name: 'Panneau Publicitaire',
      width: 1000,
      height: 600,
      screenX: 50,
      screenY: 80,
      screenWidth: 900,
      screenHeight: 450,
      borderRadius: 0,
      frameColor: '#2a2a2a',
      notch: false,
      category: 'print',
      isBillboard: true
    },
    magazine: {
      name: 'Magazine',
      width: 500,
      height: 650,
      screenX: 25,
      screenY: 25,
      screenWidth: 450,
      screenHeight: 600,
      borderRadius: 3,
      frameColor: '#ffffff',
      notch: false,
      category: 'print',
      shadow: true
    },
    // Clothing mockups
    tshirt: {
      name: 'T-Shirt',
      width: 600,
      height: 700,
      screenX: 180,
      screenY: 180,
      screenWidth: 240,
      screenHeight: 280,
      borderRadius: 0,
      frameColor: '#ffffff',
      notch: false,
      category: 'clothing',
      isTshirt: true,
      clothingColor: '#ffffff'
    },
    tshirtblack: {
      name: 'T-Shirt Noir',
      width: 600,
      height: 700,
      screenX: 180,
      screenY: 180,
      screenWidth: 240,
      screenHeight: 280,
      borderRadius: 0,
      frameColor: '#1a1a1a',
      notch: false,
      category: 'clothing',
      isTshirt: true,
      clothingColor: '#1a1a1a'
    },
    hoodie: {
      name: 'Hoodie',
      width: 650,
      height: 750,
      screenX: 200,
      screenY: 220,
      screenWidth: 250,
      screenHeight: 280,
      borderRadius: 0,
      frameColor: '#374151',
      notch: false,
      category: 'clothing',
      isHoodie: true,
      clothingColor: '#374151'
    },
    totebag: {
      name: 'Tote Bag',
      width: 500,
      height: 600,
      screenX: 100,
      screenY: 120,
      screenWidth: 300,
      screenHeight: 350,
      borderRadius: 0,
      frameColor: '#f5f0e6',
      notch: false,
      category: 'clothing',
      isToteBag: true,
      clothingColor: '#f5f0e6'
    },
    cap: {
      name: 'Casquette',
      width: 500,
      height: 400,
      screenX: 150,
      screenY: 100,
      screenWidth: 200,
      screenHeight: 120,
      borderRadius: 0,
      frameColor: '#1a1a1a',
      notch: false,
      category: 'clothing',
      isCap: true,
      clothingColor: '#1a1a1a'
    },
    mug: {
      name: 'Mug',
      width: 500,
      height: 450,
      screenX: 120,
      screenY: 100,
      screenWidth: 260,
      screenHeight: 200,
      borderRadius: 0,
      frameColor: '#ffffff',
      notch: false,
      category: 'clothing',
      isMug: true,
      clothingColor: '#ffffff'
    }
  };

  const mockupCategories = [
    { id: 'devices', name: 'Appareils', icon: 'Smartphone' },
    { id: 'print', name: 'Print & Affichage', icon: 'FileText' },
    { id: 'clothing', name: 'Vêtements & Objets', icon: 'Shirt' }
  ];

  const backgroundColors = [
    { name: 'Gris clair', value: '#f3f4f6' },
    { name: 'Blanc', value: '#ffffff' },
    { name: 'Noir', value: '#000000' },
    { name: 'Bleu', value: '#3b82f6' },
    { name: 'Violet', value: '#8b5cf6' },
    { name: 'Rose', value: '#ec4899' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Vert', value: '#22c55e' },
    { name: 'Gradient Bleu', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { name: 'Gradient Sunset', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { name: 'Gradient Ocean', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }
  ];

  const [selectedCategory, setSelectedCategory] = useState('devices');

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const filesWithId = newFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      targetFormat: getDefaultFormat(file.type),
      status: 'ready'
    }));
    setFiles(prev => [...prev, ...filesWithId]);
  };

  const getDefaultFormat = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'PNG';
    if (mimeType.startsWith('video/')) return 'MP4';
    if (mimeType.startsWith('audio/')) return 'MP3';
    return 'PNG';
  };

  const getFileCategory = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'other';
  };

  const getAvailableFormats = (mimeType) => {
    if (mimeType.startsWith('image/')) return imageFormats;
    if (mimeType.startsWith('video/')) return videoFormats;
    if (mimeType.startsWith('audio/')) return audioFormats;
    return imageFormats;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    setConverted(prev => {
      const newConverted = { ...prev };
      delete newConverted[id];
      return newConverted;
    });
  };

  const updateTargetFormat = (id, format) => {
    setFiles(prev => prev.map(f => 
      f.id === id ? { ...f, targetFormat: format } : f
    ));
  };

  const convertImage = async (fileData, targetFormat, quality) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          let mimeType;
          switch (targetFormat.toUpperCase()) {
            case 'PNG': mimeType = 'image/png'; break;
            case 'JPG': mimeType = 'image/jpeg'; break;
            case 'WEBP': mimeType = 'image/webp'; break;
            case 'GIF': mimeType = 'image/gif'; break;
            case 'BMP': mimeType = 'image/bmp'; break;
            default: mimeType = 'image/png';
          }

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Conversion failed'));
            }
          }, mimeType, quality / 100);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(fileData.file);
    });
  };

  const compressImage = async (fileData, quality) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          // Determine output format based on original or use JPEG for best compression
          let mimeType = fileData.type;
          if (mimeType === 'image/png' || mimeType === 'image/gif') {
            mimeType = 'image/webp'; // WebP offers better compression
          }

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Compression failed'));
            }
          }, mimeType, quality / 100);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(fileData.file);
    });
  };

  const processFile = async (fileData) => {
    setConverting(prev => ({ ...prev, [fileData.id]: true }));
    
    try {
      let blob;
      const category = getFileCategory(fileData.type);
      
      if (category === 'image') {
        if (activeTab === 'convert') {
          blob = await convertImage(fileData, fileData.targetFormat, 95);
        } else {
          blob = await compressImage(fileData, compressionQuality);
        }
      } else {
        // For non-image files, just return the original for now
        blob = fileData.file;
      }

      const extension = activeTab === 'convert' 
        ? fileData.targetFormat.toLowerCase() 
        : fileData.name.split('.').pop();
      
      const newFileName = fileData.name.replace(/\.[^/.]+$/, '') + 
        (activeTab === 'compress' ? '_compressed' : '_converted') + 
        '.' + extension;

      setConverted(prev => ({
        ...prev,
        [fileData.id]: {
          blob,
          fileName: newFileName,
          size: blob.size
        }
      }));

      setFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, status: 'done' } : f
      ));
    } catch (error) {
      console.error('Processing error:', error);
      setFiles(prev => prev.map(f => 
        f.id === fileData.id ? { ...f, status: 'error' } : f
      ));
    } finally {
      setConverting(prev => ({ ...prev, [fileData.id]: false }));
    }
  };

  const processAllFiles = async () => {
    for (const fileData of files) {
      if (fileData.status !== 'done') {
        await processFile(fileData);
      }
    }
  };

  const downloadFile = (id) => {
    const convertedFile = converted[id];
    if (!convertedFile) return;

    const url = URL.createObjectURL(convertedFile.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = convertedFile.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadAll = () => {
    Object.keys(converted).forEach(id => {
      downloadFile(parseInt(id));
    });
  };

  const clearAll = () => {
    setFiles([]);
    setConverted({});
    setConverting({});
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return <Image className="w-6 h-6 text-blue-500" />;
    if (mimeType.startsWith('video/')) return <Video className="w-6 h-6 text-purple-500" />;
    if (mimeType.startsWith('audio/')) return <FileAudio className="w-6 h-6 text-green-500" />;
    return <File className="w-6 h-6 text-gray-500" />;
  };

  // YouTube Functions
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatViews = (views) => {
    if (!views) return 'N/A';
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M vues`;
    }
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K vues`;
    }
    return `${views} vues`;
  };

  const fetchYoutubeInfo = async () => {
    if (!youtubeUrl.trim()) {
      setYoutubeError('Veuillez entrer une URL YouTube');
      return;
    }

    setYoutubeLoading(true);
    setYoutubeError('');
    setYoutubeInfo(null);

    try {
      const response = await fetch(`${API_URL}/api/youtube/info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: youtubeUrl })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la récupération des informations');
      }

      const data = await response.json();
      setYoutubeInfo(data);
      if (data.formats && data.formats.length > 0) {
        setSelectedFormat(data.formats[0].format_id);
      }
    } catch (error) {
      setYoutubeError(error.message || 'URL invalide ou vidéo non disponible');
    } finally {
      setYoutubeLoading(false);
    }
  };

  const downloadYoutubeVideo = async () => {
    if (!youtubeInfo || !selectedFormat) return;

    setDownloading(true);
    setYoutubeError('');
    setDownloadProgress('Préparation du téléchargement...');

    try {
      setDownloadProgress('Téléchargement en cours... Cela peut prendre quelques minutes.');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout
      
      const response = await fetch(`${API_URL}/api/youtube/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: youtubeUrl,
          format_id: selectedFormat,
          title: youtubeInfo.title
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors du téléchargement');
      }

      setDownloadProgress('Réception du fichier...');
      
      // Get content length for progress tracking
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      
      while(true) {
        const {done, value} = await reader.read();
        if (done) break;
        
        chunks.push(value);
        received += value.length;
        
        if (total > 0) {
          const percent = Math.round((received / total) * 100);
          setDownloadProgress(`Téléchargement: ${percent}% (${(received / 1024 / 1024).toFixed(1)} MB)`);
        } else {
          setDownloadProgress(`Téléchargement: ${(received / 1024 / 1024).toFixed(1)} MB reçus...`);
        }
      }
      
      setDownloadProgress('Finalisation...');
      
      const blob = new Blob(chunks, { type: 'video/mp4' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Sanitize filename
      const safeTitle = youtubeInfo.title.replace(/[<>:"/\\|?*]/g, '').substring(0, 100);
      a.download = `${safeTitle}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setDownloadProgress('Téléchargement terminé !');
      setTimeout(() => setDownloadProgress(''), 3000);
    } catch (error) {
      console.error('Download error:', error);
      if (error.name === 'AbortError') {
        setYoutubeError('Le téléchargement a expiré. Essayez une qualité inférieure.');
      } else {
        setYoutubeError(error.message || 'Erreur lors du téléchargement. Réessayez.');
      }
      setDownloadProgress('');
    } finally {
      setDownloading(false);
    }
  };

  const downloadYoutubeAudio = async () => {
    if (!youtubeInfo) return;

    setDownloading(true);
    setYoutubeError('');
    setDownloadProgress('Extraction audio en cours... Cela peut prendre quelques minutes.');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes timeout
      
      const response = await fetch(`${API_URL}/api/youtube/download-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: youtubeUrl }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors du téléchargement audio');
      }

      setDownloadProgress('Réception du fichier audio...');
      
      // Get content length for progress tracking
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      
      while(true) {
        const {done, value} = await reader.read();
        if (done) break;
        
        chunks.push(value);
        received += value.length;
        
        if (total > 0) {
          const percent = Math.round((received / total) * 100);
          setDownloadProgress(`Téléchargement: ${percent}% (${(received / 1024 / 1024).toFixed(1)} MB)`);
        } else {
          setDownloadProgress(`Téléchargement: ${(received / 1024 / 1024).toFixed(1)} MB reçus...`);
        }
      }
      
      setDownloadProgress('Finalisation...');
      
      const blob = new Blob(chunks, { type: 'audio/mpeg' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Sanitize filename
      const safeTitle = youtubeInfo.title.replace(/[<>:"/\\|?*]/g, '').substring(0, 100);
      a.download = `${safeTitle}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setDownloadProgress('Téléchargement terminé !');
      setTimeout(() => setDownloadProgress(''), 3000);
    } catch (error) {
      console.error('Download error:', error);
      if (error.name === 'AbortError') {
        setYoutubeError('Le téléchargement a expiré. Réessayez.');
      } else {
        setYoutubeError(error.message || 'Erreur lors du téléchargement audio. Réessayez.');
      }
      setDownloadProgress('');
    } finally {
      setDownloading(false);
    }
  };

  const resetYoutube = () => {
    setYoutubeUrl('');
    setYoutubeInfo(null);
    setYoutubeError('');
    setSelectedFormat('');
    setDownloadProgress('');
  };

  // SEO Functions
  const analyzeSEO = async () => {
    if (!seoUrl.trim()) {
      setSeoError('Veuillez entrer une URL');
      return;
    }

    setSeoLoading(true);
    setSeoError('');
    setSeoResult(null);

    try {
      const response = await fetch(`${API_URL}/api/seo/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: seoUrl })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'analyse');
      }

      const data = await response.json();
      setSeoResult(data);
    } catch (error) {
      setSeoError(error.message || 'Impossible d\'analyser ce site');
    } finally {
      setSeoLoading(false);
    }
  };

  const resetSEO = () => {
    setSeoUrl('');
    setSeoResult(null);
    setSeoError('');
    setShowAllIssues(false);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'from-green-400 to-green-600';
    if (score >= 60) return 'from-yellow-400 to-yellow-600';
    if (score >= 40) return 'from-orange-400 to-orange-600';
    return 'from-red-400 to-red-600';
  };

  const getIssueIcon = (type) => {
    switch (type) {
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getIssueBgColor = (type) => {
    switch (type) {
      case 'error': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  // Mockup Functions
  const handleMockupImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setMockupImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setMockupPreview(event.target.result);
        setGeneratedMockup(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMockupDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setMockupImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setMockupPreview(event.target.result);
        setGeneratedMockup(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateMockup = () => {
    if (!mockupPreview) return;

    setGeneratingMockup(true);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const device = deviceConfigs[selectedDevice];

    // Set canvas size with padding
    const padding = 80;
    canvas.width = device.width + padding * 2;
    canvas.height = device.height + padding * 2;

    // Draw background
    if (mockupBackground.startsWith('linear-gradient')) {
      const gradientMatch = mockupBackground.match(/linear-gradient\((\d+)deg,\s*(.+)\)/);
      if (gradientMatch) {
        const angle = parseInt(gradientMatch[1]);
        const colorStops = gradientMatch[2].split(',').map(s => s.trim());
        const angleRad = (angle - 90) * Math.PI / 180;
        const x1 = canvas.width / 2 - Math.cos(angleRad) * canvas.width / 2;
        const y1 = canvas.height / 2 - Math.sin(angleRad) * canvas.height / 2;
        const x2 = canvas.width / 2 + Math.cos(angleRad) * canvas.width / 2;
        const y2 = canvas.height / 2 + Math.sin(angleRad) * canvas.height / 2;
        const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
        colorStops.forEach((stop, index) => {
          const colorMatch = stop.match(/(#[a-fA-F0-9]+)\s*(\d+)?%?/);
          if (colorMatch) {
            gradient.addColorStop(index / (colorStops.length - 1), colorMatch[1]);
          }
        });
        ctx.fillStyle = gradient;
      }
    } else {
      ctx.fillStyle = mockupBackground;
    }
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const frameX = padding;
    const frameY = padding;

    // Load and draw the user's image
    const img = new window.Image();
    img.onload = () => {
      // Draw based on category
      if (device.category === 'clothing') {
        drawClothingMockup(ctx, img, device, frameX, frameY);
      } else if (device.category === 'print') {
        drawPrintMockup(ctx, img, device, frameX, frameY);
      } else {
        drawDeviceMockup(ctx, img, device, frameX, frameY);
      }

      const mockupDataUrl = canvas.toDataURL('image/png');
      setGeneratedMockup(mockupDataUrl);
      setGeneratingMockup(false);
    };
    img.src = mockupPreview;
  };

  const drawDeviceMockup = (ctx, img, device, frameX, frameY) => {
    // Device shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 20;

    ctx.fillStyle = device.frameColor;
    roundRect(ctx, frameX, frameY, device.width, device.height, device.borderRadius);
    ctx.fill();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    ctx.fillStyle = '#000000';
    const screenRadius = Math.max(device.borderRadius - 10, 5);
    roundRect(ctx, frameX + device.screenX, frameY + device.screenY, device.screenWidth, device.screenHeight, screenRadius);
    ctx.fill();

    drawImageToScreen(ctx, img, device, frameX, frameY, screenRadius);

    if (device.notch && selectedDevice === 'iphone') {
      ctx.fillStyle = '#000000';
      roundRect(ctx, frameX + (device.width - 130) / 2, frameY + device.screenY, 130, 35, 20);
      ctx.fill();
    }

    if (selectedDevice === 'browser') {
      ctx.fillStyle = '#f1f1f1';
      roundRect(ctx, frameX, frameY, device.width, 80, device.borderRadius, device.borderRadius, 0, 0);
      ctx.fill();
      const lightY = frameY + 35;
      ctx.fillStyle = '#ff5f57';
      ctx.beginPath(); ctx.arc(frameX + 25, lightY, 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#febc2e';
      ctx.beginPath(); ctx.arc(frameX + 50, lightY, 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#28c840';
      ctx.beginPath(); ctx.arc(frameX + 75, lightY, 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#ffffff';
      roundRect(ctx, frameX + 110, frameY + 22, device.width - 140, 36, 8);
      ctx.fill();
    }
  };

  const drawPrintMockup = (ctx, img, device, frameX, frameY) => {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetX = 10;
    ctx.shadowOffsetY = 15;

    if (device.isBusStop) {
      // Draw bus stop frame
      ctx.fillStyle = '#4a4a4a';
      roundRect(ctx, frameX, frameY, device.width, device.height, 15);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      
      // Glass panel effect
      ctx.fillStyle = '#6a8caf';
      ctx.fillRect(frameX + 20, frameY + 20, device.width - 40, device.height - 40);
      
      // Poster area
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(frameX + device.screenX, frameY + device.screenY, device.screenWidth, device.screenHeight);
      drawImageToScreen(ctx, img, device, frameX, frameY, 0);
      
      // Add some urban elements
      ctx.fillStyle = '#3a3a3a';
      ctx.fillRect(frameX + device.width - 60, frameY + device.height - 100, 40, 80);
    } else if (device.isBillboard) {
      // Draw billboard structure
      ctx.fillStyle = '#5a5a5a';
      roundRect(ctx, frameX, frameY, device.width, device.height, 5);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      
      // Billboard face
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(frameX + device.screenX, frameY + device.screenY, device.screenWidth, device.screenHeight);
      drawImageToScreen(ctx, img, device, frameX, frameY, 0);
      
      // Support poles
      ctx.fillStyle = '#4a4a4a';
      ctx.fillRect(frameX + 200, frameY + device.height, 30, 80);
      ctx.fillRect(frameX + device.width - 230, frameY + device.height, 30, 80);
    } else {
      // Regular print (flyer, poster, business card, magazine)
      ctx.fillStyle = '#ffffff';
      roundRect(ctx, frameX, frameY, device.width, device.height, device.borderRadius);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      
      drawImageToScreen(ctx, img, device, frameX, frameY, device.borderRadius);
    }
  };

  const drawClothingMockup = (ctx, img, device, frameX, frameY) => {
    const color = device.clothingColor || '#ffffff';
    
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = 25;
    ctx.shadowOffsetY = 10;

    if (device.isTshirt) {
      // Draw T-shirt shape
      ctx.fillStyle = color;
      ctx.beginPath();
      // Neck
      ctx.moveTo(frameX + 230, frameY + 50);
      ctx.quadraticCurveTo(frameX + 300, frameY + 30, frameX + 370, frameY + 50);
      // Right shoulder and sleeve
      ctx.lineTo(frameX + 500, frameY + 80);
      ctx.lineTo(frameX + 520, frameY + 200);
      ctx.lineTo(frameX + 450, frameY + 220);
      // Right side
      ctx.lineTo(frameX + 450, frameY + 650);
      // Bottom
      ctx.lineTo(frameX + 150, frameY + 650);
      // Left side
      ctx.lineTo(frameX + 150, frameY + 220);
      // Left sleeve
      ctx.lineTo(frameX + 80, frameY + 200);
      ctx.lineTo(frameX + 100, frameY + 80);
      ctx.closePath();
      ctx.fill();
      
      ctx.shadowColor = 'transparent';
      
      // Add subtle shading
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      ctx.fillRect(frameX + 150, frameY + 220, 50, 400);
      ctx.fillRect(frameX + 400, frameY + 220, 50, 400);
      
      // Draw the design
      drawImageToScreen(ctx, img, device, frameX, frameY, 0);
      
    } else if (device.isHoodie) {
      ctx.fillStyle = color;
      ctx.beginPath();
      // Hood
      ctx.moveTo(frameX + 200, frameY + 50);
      ctx.quadraticCurveTo(frameX + 325, frameY + 10, frameX + 450, frameY + 50);
      ctx.quadraticCurveTo(frameX + 480, frameY + 100, frameX + 470, frameY + 180);
      // Right shoulder
      ctx.lineTo(frameX + 550, frameY + 200);
      ctx.lineTo(frameX + 570, frameY + 350);
      ctx.lineTo(frameX + 500, frameY + 370);
      ctx.lineTo(frameX + 500, frameY + 700);
      ctx.lineTo(frameX + 150, frameY + 700);
      ctx.lineTo(frameX + 150, frameY + 370);
      ctx.lineTo(frameX + 80, frameY + 350);
      ctx.lineTo(frameX + 100, frameY + 200);
      ctx.lineTo(frameX + 180, frameY + 180);
      ctx.quadraticCurveTo(frameX + 170, frameY + 100, frameX + 200, frameY + 50);
      ctx.closePath();
      ctx.fill();
      
      ctx.shadowColor = 'transparent';
      
      // Pocket
      ctx.fillStyle = 'rgba(0,0,0,0.1)';
      roundRect(ctx, frameX + 200, frameY + 500, 250, 100, 10);
      ctx.fill();
      
      drawImageToScreen(ctx, img, device, frameX, frameY, 0);
      
    } else if (device.isToteBag) {
      ctx.fillStyle = color;
      // Bag body
      ctx.beginPath();
      ctx.moveTo(frameX + 80, frameY + 100);
      ctx.lineTo(frameX + 420, frameY + 100);
      ctx.lineTo(frameX + 400, frameY + 550);
      ctx.lineTo(frameX + 100, frameY + 550);
      ctx.closePath();
      ctx.fill();
      
      ctx.shadowColor = 'transparent';
      
      // Handles
      ctx.strokeStyle = color;
      ctx.lineWidth = 15;
      ctx.beginPath();
      ctx.moveTo(frameX + 150, frameY + 100);
      ctx.quadraticCurveTo(frameX + 150, frameY + 30, frameX + 200, frameY + 30);
      ctx.quadraticCurveTo(frameX + 250, frameY + 30, frameX + 250, frameY + 100);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(frameX + 250, frameY + 100);
      ctx.quadraticCurveTo(frameX + 250, frameY + 30, frameX + 300, frameY + 30);
      ctx.quadraticCurveTo(frameX + 350, frameY + 30, frameX + 350, frameY + 100);
      ctx.stroke();
      
      drawImageToScreen(ctx, img, device, frameX, frameY, 0);
      
    } else if (device.isCap) {
      ctx.fillStyle = color;
      // Cap crown
      ctx.beginPath();
      ctx.ellipse(frameX + 250, frameY + 180, 180, 100, 0, 0, Math.PI * 2);
      ctx.fill();
      
      // Visor
      ctx.beginPath();
      ctx.ellipse(frameX + 250, frameY + 250, 200, 60, 0, 0, Math.PI);
      ctx.fill();
      
      ctx.shadowColor = 'transparent';
      
      // Front panel highlight
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath();
      ctx.ellipse(frameX + 250, frameY + 160, 100, 70, 0, Math.PI, 0);
      ctx.fill();
      
      drawImageToScreen(ctx, img, device, frameX, frameY, 0);
      
    } else if (device.isMug) {
      ctx.fillStyle = color;
      // Mug body
      ctx.beginPath();
      ctx.ellipse(frameX + 250, frameY + 100, 130, 40, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(frameX + 120, frameY + 100, 260, 280);
      ctx.beginPath();
      ctx.ellipse(frameX + 250, frameY + 380, 130, 40, 0, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.shadowColor = 'transparent';
      
      // Handle
      ctx.strokeStyle = color;
      ctx.lineWidth = 25;
      ctx.beginPath();
      ctx.arc(frameX + 400, frameY + 240, 60, -Math.PI/2, Math.PI/2);
      ctx.stroke();
      
      // Inner shadow
      ctx.fillStyle = 'rgba(0,0,0,0.1)';
      ctx.beginPath();
      ctx.ellipse(frameX + 250, frameY + 100, 100, 30, 0, 0, Math.PI * 2);
      ctx.fill();
      
      drawImageToScreen(ctx, img, device, frameX, frameY, 0);
    }
  };

  const drawImageToScreen = (ctx, img, device, frameX, frameY, screenRadius) => {
    const imgRatio = img.width / img.height;
    const screenRatio = device.screenWidth / device.screenHeight;
    
    let drawWidth, drawHeight, drawX, drawY;
    
    if (imgRatio > screenRatio) {
      drawHeight = device.screenHeight;
      drawWidth = drawHeight * imgRatio;
      drawX = frameX + device.screenX - (drawWidth - device.screenWidth) / 2;
      drawY = frameY + device.screenY;
    } else {
      drawWidth = device.screenWidth;
      drawHeight = drawWidth / imgRatio;
      drawX = frameX + device.screenX;
      drawY = frameY + device.screenY - (drawHeight - device.screenHeight) / 2;
    }

    ctx.save();
    ctx.beginPath();
    if (screenRadius > 0) {
      roundRect(ctx, frameX + device.screenX, frameY + device.screenY, device.screenWidth, device.screenHeight, screenRadius);
    } else {
      ctx.rect(frameX + device.screenX, frameY + device.screenY, device.screenWidth, device.screenHeight);
    }
    ctx.clip();
    ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    ctx.restore();
  };

  // Helper function to draw rounded rectangles
  const roundRect = (ctx, x, y, width, height, radius, tl, tr, br, bl) => {
    if (typeof radius === 'number') {
      tl = tr = br = bl = radius;
    }
    ctx.beginPath();
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + width - tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + tr);
    ctx.lineTo(x + width, y + height - br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - br, y + height);
    ctx.lineTo(x + bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - bl);
    ctx.lineTo(x, y + tl);
    ctx.quadraticCurveTo(x, y, x + tl, y);
    ctx.closePath();
  };

  const downloadMockup = () => {
    if (!generatedMockup) return;
    
    const link = document.createElement('a');
    link.href = generatedMockup;
    link.download = `mockup-${selectedDevice}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetMockup = () => {
    setMockupImage(null);
    setMockupPreview(null);
    setGeneratedMockup(null);
  };

  return (
    <div className="min-h-screen">
      {/* Section Hero */}
      <section className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_70%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <AnimatedText 
              className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6"
              isH1={true}
            >
              Outils <span className="title-gradient">Gratuits</span>
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-600 leading-relaxed font-montserrat"
              delay={200}
            >
              Convertissez et compressez vos fichiers gratuitement, sans limite de taille. 
              100% gratuit, 100% privé - vos fichiers restent sur votre appareil.
            </AnimatedText>
          </div>
        </div>
      </section>

      {/* Main Tool Section */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-full inline-flex flex-wrap justify-center gap-1">
              <button
                onClick={() => setActiveTab('convert')}
                className={`px-4 sm:px-6 py-3 rounded-full font-montserrat font-medium transition-all duration-200 ${
                  activeTab === 'convert' 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileType className="w-5 h-5 inline mr-2" />
                Convertir
              </button>
              <button
                onClick={() => setActiveTab('compress')}
                className={`px-4 sm:px-6 py-3 rounded-full font-montserrat font-medium transition-all duration-200 ${
                  activeTab === 'compress' 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Settings className="w-5 h-5 inline mr-2" />
                Compresser
              </button>
              <button
                onClick={() => setActiveTab('youtube')}
                className={`px-4 sm:px-6 py-3 rounded-full font-montserrat font-medium transition-all duration-200 ${
                  activeTab === 'youtube' 
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Youtube className="w-5 h-5 inline mr-2" />
                YouTube
              </button>
              <button
                onClick={() => setActiveTab('seo')}
                className={`px-4 sm:px-6 py-3 rounded-full font-montserrat font-medium transition-all duration-200 ${
                  activeTab === 'seo' 
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BarChart3 className="w-5 h-5 inline mr-2" />
                SEO
              </button>
              <button
                onClick={() => setActiveTab('mockup')}
                className={`px-4 sm:px-6 py-3 rounded-full font-montserrat font-medium transition-all duration-200 ${
                  activeTab === 'mockup' 
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Smartphone className="w-5 h-5 inline mr-2" />
                Mockup
              </button>
            </div>
          </div>

          {/* Mockup Generator Section */}
          {activeTab === 'mockup' && (
            <>
              <FloatingCard className="luxury-card mb-8">
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Smartphone className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-amoria font-bold text-gray-900 mb-2">
                      Générateur de Mockup
                    </h3>
                    <p className="text-gray-600 font-montserrat">
                      Créez des mockups professionnels pour vos designs en quelques clics
                    </p>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Left Column - Upload & Settings */}
                    <div className="space-y-6">
                      {/* Image Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-montserrat">
                          1. Importez votre image
                        </label>
                        <div
                          onDrop={handleMockupDrop}
                          onDragOver={(e) => e.preventDefault()}
                          onClick={() => document.getElementById('mockupFileInput').click()}
                          className="border-2 border-dashed border-purple-300 rounded-xl p-8 text-center hover:border-purple-500 hover:bg-purple-50/50 transition-all cursor-pointer"
                        >
                          <input
                            id="mockupFileInput"
                            type="file"
                            accept="image/*"
                            onChange={handleMockupImageUpload}
                            className="hidden"
                          />
                          {mockupPreview ? (
                            <div>
                              <img 
                                src={mockupPreview} 
                                alt="Preview" 
                                className="max-h-40 mx-auto rounded-lg shadow mb-3"
                              />
                              <p className="text-sm text-gray-600 font-montserrat">
                                Cliquez pour changer l'image
                              </p>
                            </div>
                          ) : (
                            <>
                              <ImageIcon className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                              <p className="text-gray-700 font-montserrat font-medium">
                                Glissez une image ou cliquez
                              </p>
                              <p className="text-sm text-gray-500 font-montserrat mt-1">
                                PNG, JPG, WEBP
                              </p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Category Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-montserrat">
                          2. Choisissez la catégorie
                        </label>
                        <div className="flex gap-2 mb-4">
                          <button
                            onClick={() => { setSelectedCategory('devices'); setSelectedDevice('iphone'); setGeneratedMockup(null); }}
                            className={`flex-1 p-3 rounded-xl border-2 transition-all flex flex-col items-center ${
                              selectedCategory === 'devices' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <Smartphone className="w-6 h-6 text-gray-700" />
                            <span className="text-xs mt-1 font-montserrat">Appareils</span>
                          </button>
                          <button
                            onClick={() => { setSelectedCategory('print'); setSelectedDevice('flyer'); setGeneratedMockup(null); }}
                            className={`flex-1 p-3 rounded-xl border-2 transition-all flex flex-col items-center ${
                              selectedCategory === 'print' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <FileText className="w-6 h-6 text-gray-700" />
                            <span className="text-xs mt-1 font-montserrat">Print</span>
                          </button>
                          <button
                            onClick={() => { setSelectedCategory('clothing'); setSelectedDevice('tshirt'); setGeneratedMockup(null); }}
                            className={`flex-1 p-3 rounded-xl border-2 transition-all flex flex-col items-center ${
                              selectedCategory === 'clothing' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                            }`}
                          >
                            <Frame className="w-6 h-6 text-gray-700" />
                            <span className="text-xs mt-1 font-montserrat">Vêtements</span>
                          </button>
                        </div>

                        {/* Device Selection based on category */}
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                          {Object.entries(deviceConfigs)
                            .filter(([key, device]) => device.category === selectedCategory)
                            .map(([key, device]) => (
                            <button
                              key={key}
                              onClick={() => {
                                setSelectedDevice(key);
                                setGeneratedMockup(null);
                              }}
                              className={`p-3 rounded-xl border-2 transition-all ${
                                selectedDevice === key 
                                  ? 'border-purple-500 bg-purple-50' 
                                  : 'border-gray-200 hover:border-purple-300'
                              }`}
                            >
                              {/* Device icons */}
                              {key === 'iphone' && <Smartphone className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'macbook' && <Laptop className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'ipad' && <Tablet className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'android' && <Smartphone className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'browser' && <Monitor className="w-5 h-5 mx-auto text-gray-700" />}
                              {/* Print icons */}
                              {key === 'businesscard' && <FileText className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'flyer' && <FileText className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'poster' && <ImageIcon className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'busstop' && <Frame className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'billboard' && <Monitor className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'magazine' && <FileText className="w-5 h-5 mx-auto text-gray-700" />}
                              {/* Clothing icons */}
                              {(key === 'tshirt' || key === 'tshirtblack') && <Frame className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'hoodie' && <Frame className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'totebag' && <Frame className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'cap' && <Frame className="w-5 h-5 mx-auto text-gray-700" />}
                              {key === 'mug' && <Frame className="w-5 h-5 mx-auto text-gray-700" />}
                              <p className="text-xs mt-1 font-montserrat text-gray-600 truncate">
                                {device.name}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Background Color */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 font-montserrat">
                          3. Couleur de fond
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {backgroundColors.map((color) => (
                            <button
                              key={color.value}
                              onClick={() => {
                                setMockupBackground(color.value);
                                setGeneratedMockup(null);
                              }}
                              className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                mockupBackground === color.value 
                                  ? 'border-purple-500 scale-110' 
                                  : 'border-gray-300 hover:scale-105'
                              }`}
                              style={{ 
                                background: color.value.startsWith('linear') ? color.value : color.value 
                              }}
                              title={color.name}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Generate Button */}
                      <ShimmerButton
                        onClick={generateMockup}
                        disabled={!mockupPreview || generatingMockup}
                        className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white py-4"
                      >
                        {generatingMockup ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Génération...
                          </>
                        ) : (
                          <>
                            <Frame className="w-5 h-5 mr-2" />
                            Générer le Mockup
                          </>
                        )}
                      </ShimmerButton>
                    </div>

                    {/* Right Column - Preview */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 font-montserrat">
                        Aperçu
                      </label>
                      <div 
                        className="bg-gray-100 rounded-xl p-4 min-h-[400px] flex items-center justify-center"
                        style={{ 
                          background: generatedMockup ? mockupBackground : '#f3f4f6'
                        }}
                      >
                        {generatedMockup ? (
                          <img 
                            src={generatedMockup} 
                            alt="Generated Mockup" 
                            className="max-w-full max-h-[500px] rounded-lg shadow-2xl"
                          />
                        ) : mockupPreview ? (
                          <div className="text-center text-gray-500">
                            <Frame className="w-16 h-16 mx-auto mb-3 text-gray-400" />
                            <p className="font-montserrat">
                              Cliquez sur "Générer le Mockup"
                            </p>
                          </div>
                        ) : (
                          <div className="text-center text-gray-400">
                            <Smartphone className="w-16 h-16 mx-auto mb-3" />
                            <p className="font-montserrat">
                              Importez une image pour commencer
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Download Button */}
                      {generatedMockup && (
                        <div className="flex gap-3 mt-4">
                          <ShimmerButton
                            onClick={downloadMockup}
                            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-3"
                          >
                            <Download className="w-5 h-5 mr-2" />
                            Télécharger PNG
                          </ShimmerButton>
                          <Button
                            variant="outline"
                            onClick={resetMockup}
                            className="px-6"
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </FloatingCard>

              {/* Mockup Features */}
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <FloatingCard className="luxury-card text-center" delay={0.1}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Smartphone className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      5 Appareils
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      iPhone, MacBook, iPad, Android et navigateur web.
                    </p>
                  </CardContent>
                </FloatingCard>

                <FloatingCard className="luxury-card text-center" delay={0.2}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Frame className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Fonds Personnalisés
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Couleurs solides ou dégradés pour sublimer vos mockups.
                    </p>
                  </CardContent>
                </FloatingCard>

                <FloatingCard className="luxury-card text-center" delay={0.3}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Download className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Export HD
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Téléchargez vos mockups en haute résolution PNG.
                    </p>
                  </CardContent>
                </FloatingCard>
              </div>
            </>
          )}

          {/* SEO Analyzer Section */}
          {activeTab === 'seo' && (
            <>
              <FloatingCard className="luxury-card mb-8">
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BarChart3 className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-amoria font-bold text-gray-900 mb-2">
                      Analyseur SEO
                    </h3>
                    <p className="text-gray-600 font-montserrat">
                      Analysez gratuitement le référencement de n'importe quel site web
                    </p>
                  </div>

                  {/* URL Input */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <Input
                      type="text"
                      placeholder="Entrez l'URL du site (ex: example.com)"
                      value={seoUrl}
                      onChange={(e) => setSeoUrl(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && analyzeSEO()}
                      className="flex-1 h-12 text-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                    <ShimmerButton
                      onClick={analyzeSEO}
                      disabled={seoLoading}
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 h-12"
                    >
                      {seoLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Search className="w-5 h-5 mr-2" />
                          Analyser
                        </>
                      )}
                    </ShimmerButton>
                  </div>

                  {/* Error Message */}
                  {seoError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-montserrat">{seoError}</span>
                      </div>
                    </div>
                  )}

                  {/* SEO Results */}
                  {seoResult && (
                    <div className="space-y-6">
                      {/* Score Circle */}
                      <div className="text-center py-8 bg-gray-50 rounded-2xl">
                        <div className={`w-32 h-32 mx-auto rounded-full bg-gradient-to-br ${getScoreBgColor(seoResult.score)} flex items-center justify-center shadow-lg mb-4`}>
                          <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center">
                            <span className={`text-4xl font-bold ${getScoreColor(seoResult.score)}`}>
                              {seoResult.score}
                            </span>
                          </div>
                        </div>
                        <h4 className="text-xl font-amoria font-bold text-gray-900 mb-1">
                          Score SEO
                        </h4>
                        <p className="text-gray-600 font-montserrat text-sm">
                          {seoResult.score >= 80 ? 'Excellent' : seoResult.score >= 60 ? 'Bon' : seoResult.score >= 40 ? 'À améliorer' : 'Critique'}
                        </p>
                        <p className="text-gray-500 font-montserrat text-xs mt-2">
                          Temps de chargement: {seoResult.load_time_ms}ms
                        </p>
                      </div>

                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 rounded-xl p-4 text-center">
                          <FileText className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-gray-900">{seoResult.word_count}</p>
                          <p className="text-xs text-gray-600 font-montserrat">Mots</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 text-center">
                          <ImageIcon className="w-6 h-6 text-green-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-gray-900">{seoResult.images_count}</p>
                          <p className="text-xs text-gray-600 font-montserrat">Images</p>
                        </div>
                        <div className="bg-purple-50 rounded-xl p-4 text-center">
                          <Link2 className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-gray-900">{seoResult.internal_links}</p>
                          <p className="text-xs text-gray-600 font-montserrat">Liens internes</p>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-4 text-center">
                          <Globe className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                          <p className="text-2xl font-bold text-gray-900">{seoResult.external_links}</p>
                          <p className="text-xs text-gray-600 font-montserrat">Liens externes</p>
                        </div>
                      </div>

                      {/* Title & Meta */}
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h4 className="text-lg font-amoria font-bold text-gray-900 mb-4">Balises principales</h4>
                        
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-700">Title</span>
                              <span className={`text-xs ${seoResult.title_length >= 30 && seoResult.title_length <= 60 ? 'text-green-600' : 'text-yellow-600'}`}>
                                {seoResult.title_length} caractères
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 bg-white rounded-lg p-3 border">
                              {seoResult.title || <span className="text-red-500 italic">Non défini</span>}
                            </p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-700">Meta Description</span>
                              <span className={`text-xs ${seoResult.meta_description_length >= 120 && seoResult.meta_description_length <= 160 ? 'text-green-600' : 'text-yellow-600'}`}>
                                {seoResult.meta_description_length} caractères
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 bg-white rounded-lg p-3 border">
                              {seoResult.meta_description || <span className="text-red-500 italic">Non définie</span>}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Headings */}
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h4 className="text-lg font-amoria font-bold text-gray-900 mb-4">Structure des titres</h4>
                        <div className="flex flex-wrap gap-4">
                          <div className={`px-4 py-2 rounded-lg ${seoResult.h1_count === 1 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            <span className="font-bold">H1:</span> {seoResult.h1_count}
                          </div>
                          <div className="px-4 py-2 rounded-lg bg-blue-100 text-blue-700">
                            <span className="font-bold">H2:</span> {seoResult.h2_count}
                          </div>
                          <div className="px-4 py-2 rounded-lg bg-purple-100 text-purple-700">
                            <span className="font-bold">H3:</span> {seoResult.h3_count}
                          </div>
                        </div>
                        {seoResult.h1_tags && seoResult.h1_tags.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-gray-600 font-medium mb-2">Contenu H1:</p>
                            {seoResult.h1_tags.map((h1, idx) => (
                              <p key={idx} className="text-sm text-gray-700 bg-white rounded p-2 mb-1 border">
                                {h1}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Technical Checks */}
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h4 className="text-lg font-amoria font-bold text-gray-900 mb-4">Vérifications techniques</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[
                            { label: 'Viewport', value: seoResult.has_viewport },
                            { label: 'Charset', value: seoResult.has_charset },
                            { label: 'Lang', value: seoResult.has_lang },
                            { label: 'Favicon', value: seoResult.has_favicon },
                            { label: 'Canonical', value: seoResult.has_canonical },
                            { label: 'Robots', value: seoResult.has_robots },
                          ].map((check, idx) => (
                            <div key={idx} className={`flex items-center gap-2 p-3 rounded-lg ${check.value ? 'bg-green-50' : 'bg-red-50'}`}>
                              {check.value ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-500" />
                              )}
                              <span className="text-sm font-montserrat">{check.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Issues */}
                      {seoResult.issues && seoResult.issues.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-amoria font-bold text-gray-900">
                              Problèmes détectés ({seoResult.issues.length})
                            </h4>
                            {seoResult.issues.length > 3 && (
                              <button
                                onClick={() => setShowAllIssues(!showAllIssues)}
                                className="text-blue-600 text-sm font-montserrat flex items-center gap-1 hover:underline"
                              >
                                {showAllIssues ? 'Voir moins' : 'Voir tout'}
                                {showAllIssues ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            )}
                          </div>
                          <div className="space-y-3">
                            {(showAllIssues ? seoResult.issues : seoResult.issues.slice(0, 3)).map((issue, idx) => (
                              <div key={idx} className={`border rounded-lg p-4 ${getIssueBgColor(issue.type)}`}>
                                <div className="flex items-start gap-3">
                                  {getIssueIcon(issue.type)}
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900 text-sm">{issue.message}</p>
                                    <p className="text-gray-600 text-xs mt-1">{issue.recommendation}</p>
                                  </div>
                                  <span className="text-xs bg-white px-2 py-1 rounded font-montserrat">
                                    {issue.category}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Reset Button */}
                      <div className="text-center">
                        <Button
                          variant="outline"
                          onClick={resetSEO}
                          className="px-8 py-3"
                        >
                          <Trash2 className="w-5 h-5 mr-2" />
                          Nouvelle analyse
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </FloatingCard>

              {/* SEO Features */}
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <FloatingCard className="luxury-card text-center" delay={0.1}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Analyse Complète
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Title, meta description, headings, images, liens et plus encore.
                    </p>
                  </CardContent>
                </FloatingCard>

                <FloatingCard className="luxury-card text-center" delay={0.2}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Instantané
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Résultats en quelques secondes avec score et recommandations.
                    </p>
                  </CardContent>
                </FloatingCard>

                <FloatingCard className="luxury-card text-center" delay={0.3}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Smartphone className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Mobile-Friendly
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Vérification des balises viewport et compatibilité mobile.
                    </p>
                  </CardContent>
                </FloatingCard>
              </div>
            </>
          )}

          {/* YouTube Downloader Section */}
          {activeTab === 'youtube' && (
            <>
              <FloatingCard className="luxury-card mb-8">
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Youtube className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-amoria font-bold text-gray-900 mb-2">
                      Téléchargeur YouTube
                    </h3>
                    <p className="text-gray-600 font-montserrat">
                      Téléchargez des vidéos YouTube en haute qualité (jusqu'à 4K)
                    </p>
                  </div>
                  
                  {/* URL Input */}
                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <Input
                      type="text"
                      placeholder="Collez l'URL YouTube ici..."
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && fetchYoutubeInfo()}
                      className="flex-1 h-12 text-lg border-gray-300 focus:border-red-500 focus:ring-red-500"
                    />
                    <ShimmerButton
                      onClick={fetchYoutubeInfo}
                      disabled={youtubeLoading}
                      className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 h-12"
                    >
                      {youtubeLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Search className="w-5 h-5 mr-2" />
                          Rechercher
                        </>
                      )}
                    </ShimmerButton>
                  </div>

                  {/* Error Message */}
                  {youtubeError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-montserrat">{youtubeError}</span>
                      </div>
                    </div>
                  )}

                  {/* Video Info */}
                  {youtubeInfo && (
                    <div className="bg-gray-50 rounded-2xl p-6">
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* Thumbnail */}
                        <div className="flex-shrink-0">
                          <img
                            src={youtubeInfo.thumbnail}
                            alt={youtubeInfo.title}
                            className="w-full md:w-64 h-auto rounded-xl shadow-lg"
                          />
                        </div>

                        {/* Video Details */}
                        <div className="flex-1">
                          <h4 className="text-xl font-amoria font-bold text-gray-900 mb-2 line-clamp-2">
                            {youtubeInfo.title}
                          </h4>
                          <p className="text-gray-600 font-montserrat mb-4">
                            {youtubeInfo.channel}
                          </p>
                          
                          <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-500 font-montserrat">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {formatDuration(youtubeInfo.duration)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {formatViews(youtubeInfo.view_count)}
                            </span>
                          </div>

                          {/* Format Selection */}
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2 font-montserrat">
                              Qualité vidéo
                            </label>
                            <Select
                              value={selectedFormat}
                              onValueChange={setSelectedFormat}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Sélectionnez la qualité" />
                              </SelectTrigger>
                              <SelectContent>
                                {youtubeInfo.formats.map((format) => (
                                  <SelectItem key={format.format_id} value={format.format_id}>
                                    {format.quality_label} ({format.ext.toUpperCase()})
                                    {format.filesize && ` - ${(format.filesize / 1024 / 1024).toFixed(1)} MB`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Download Progress */}
                          {downloadProgress && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                              <div className="flex items-center gap-2 text-amber-700">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span className="font-montserrat">{downloadProgress}</span>
                              </div>
                            </div>
                          )}

                          {/* Download Buttons */}
                          <div className="flex flex-wrap gap-3">
                            <ShimmerButton
                              onClick={downloadYoutubeVideo}
                              disabled={downloading || !selectedFormat}
                              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-3"
                            >
                              <Download className="w-5 h-5 mr-2" />
                              Télécharger Vidéo
                            </ShimmerButton>
                            
                            <ShimmerButton
                              onClick={downloadYoutubeAudio}
                              disabled={downloading}
                              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3"
                            >
                              <Music className="w-5 h-5 mr-2" />
                              Audio MP3
                            </ShimmerButton>

                            <Button
                              variant="outline"
                              onClick={resetYoutube}
                              className="px-6 py-3"
                            >
                              <Trash2 className="w-5 h-5 mr-2" />
                              Réinitialiser
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </FloatingCard>

              {/* YouTube Features */}
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <FloatingCard className="luxury-card text-center" delay={0.1}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Haute Qualité
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Téléchargez en 4K, 1080p, 720p ou la qualité de votre choix.
                    </p>
                  </CardContent>
                </FloatingCard>

                <FloatingCard className="luxury-card text-center" delay={0.2}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Music className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Audio MP3
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Extrayez l'audio en MP3 320kbps pour vos playlists.
                    </p>
                  </CardContent>
                </FloatingCard>

                <FloatingCard className="luxury-card text-center" delay={0.3}>
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Download className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                      Sans Limite
                    </h3>
                    <p className="text-gray-600 font-montserrat text-sm">
                      Téléchargez autant de vidéos que vous voulez, gratuitement.
                    </p>
                  </CardContent>
                </FloatingCard>
              </div>
            </>
          )}

          {/* File Converter Section (existing code) */}
          {(activeTab === 'convert' || activeTab === 'compress') && (
            <>
              {/* Drop Zone */}
          <FloatingCard className="luxury-card mb-8">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-amber-300 rounded-2xl p-12 text-center hover:border-amber-500 hover:bg-amber-50/50 transition-all duration-300 cursor-pointer"
              onClick={() => document.getElementById('fileInput').click()}
            >
              <input
                id="fileInput"
                type="file"
                multiple
                onChange={handleFileInput}
                className="hidden"
                accept="image/*,video/*,audio/*"
              />
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileType className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-amoria font-bold text-gray-900 mb-2">
                Glissez vos fichiers ici
              </h3>
              <p className="text-gray-600 font-montserrat mb-4">
                ou cliquez pour sélectionner
              </p>
              <p className="text-sm text-gray-500 font-montserrat">
                Images (PNG, JPG, WEBP, GIF) • Vidéos (MP4, WEBM) • Audio (MP3, WAV)
              </p>
              <p className="text-xs text-amber-600 font-montserrat mt-2 font-medium">
                Aucune limite de taille !
              </p>
            </div>
          </FloatingCard>

          {/* Compression Quality Slider (only for compress tab) */}
          {activeTab === 'compress' && files.length > 0 && (
            <FloatingCard className="luxury-card mb-8">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-amoria font-bold text-gray-900">
                    Qualité de compression
                  </h3>
                  <span className="text-2xl font-bold text-amber-600">{compressionQuality}%</span>
                </div>
                <Slider
                  value={[compressionQuality]}
                  onValueChange={(value) => setCompressionQuality(value[0])}
                  max={100}
                  min={10}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between mt-2 text-sm text-gray-500 font-montserrat">
                  <span>Plus petit fichier</span>
                  <span>Meilleure qualité</span>
                </div>
              </CardContent>
            </FloatingCard>
          )}

          {/* File List */}
          {files.length > 0 && (
            <FloatingCard className="luxury-card mb-8">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-amoria font-bold text-gray-900">
                    Fichiers ({files.length})
                  </h3>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAll}
                      className="text-gray-600 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Tout effacer
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {files.map((fileData) => (
                    <div
                      key={fileData.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      {/* File Icon */}
                      <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        {getFileIcon(fileData.type)}
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-montserrat font-medium text-gray-900 truncate">
                          {fileData.name}
                        </p>
                        <p className="text-sm text-gray-500 font-montserrat">
                          {formatFileSize(fileData.size)}
                          {converted[fileData.id] && (
                            <span className="text-green-600 ml-2">
                              → {formatFileSize(converted[fileData.id].size)}
                              {converted[fileData.id].size < fileData.size && (
                                <span className="ml-1">
                                  (-{Math.round((1 - converted[fileData.id].size / fileData.size) * 100)}%)
                                </span>
                              )}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Format Selector (only for convert tab) */}
                      {activeTab === 'convert' && (
                        <Select
                          value={fileData.targetFormat}
                          onValueChange={(value) => updateTargetFormat(fileData.id, value)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableFormats(fileData.type).map((format) => (
                              <SelectItem key={format} value={format}>
                                {format}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {/* Status / Actions */}
                      <div className="flex items-center gap-2">
                        {converting[fileData.id] ? (
                          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
                        ) : fileData.status === 'done' ? (
                          <>
                            <CheckCircle className="w-6 h-6 text-green-500" />
                            <Button
                              size="sm"
                              onClick={() => downloadFile(fileData.id)}
                              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </>
                        ) : fileData.status === 'error' ? (
                          <AlertCircle className="w-6 h-6 text-red-500" />
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => processFile(fileData)}
                          >
                            {activeTab === 'convert' ? 'Convertir' : 'Compresser'}
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFile(fileData.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-center gap-4 mt-8">
                  <ShimmerButton
                    onClick={processAllFiles}
                    className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8 py-4"
                  >
                    {activeTab === 'convert' ? 'Tout Convertir' : 'Tout Compresser'}
                  </ShimmerButton>
                  
                  {Object.keys(converted).length > 0 && (
                    <ShimmerButton
                      onClick={downloadAll}
                      variant="outline"
                      className="border-amber-500 text-amber-600 hover:bg-amber-50 px-8 py-4"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Tout Télécharger
                    </ShimmerButton>
                  )}
                </div>
              </CardContent>
            </FloatingCard>
          )}

          {/* Features */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <FloatingCard className="luxury-card text-center" delay={0.1}>
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                  100% Gratuit
                </h3>
                <p className="text-gray-600 font-montserrat text-sm">
                  Aucun frais caché, aucune inscription requise. Utilisez nos outils sans limite.
                </p>
              </CardContent>
            </FloatingCard>

            <FloatingCard className="luxury-card text-center" delay={0.2}>
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                  Sans Limite
                </h3>
                <p className="text-gray-600 font-montserrat text-sm">
                  Aucune restriction de taille. Convertissez des fichiers de plusieurs Go sans problème.
                </p>
              </CardContent>
            </FloatingCard>

            <FloatingCard className="luxury-card text-center" delay={0.3}>
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileType className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-amoria font-bold text-gray-900 mb-2">
                  Privé & Sécurisé
                </h3>
                <p className="text-gray-600 font-montserrat text-sm">
                  Tout se passe dans votre navigateur. Vos fichiers ne quittent jamais votre appareil.
                </p>
              </CardContent>
            </FloatingCard>
          </div>

          {/* Supported Formats */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-amoria font-bold text-gray-900 mb-8">
              Formats Supportés
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 bg-blue-50 rounded-2xl">
                <Image className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                <h4 className="font-amoria font-bold text-gray-900 mb-2">Images</h4>
                <p className="text-sm text-gray-600 font-montserrat">
                  PNG, JPG, JPEG, WEBP, GIF, BMP
                </p>
              </div>
              <div className="p-6 bg-purple-50 rounded-2xl">
                <Video className="w-10 h-10 text-purple-500 mx-auto mb-3" />
                <h4 className="font-amoria font-bold text-gray-900 mb-2">Vidéos</h4>
                <p className="text-sm text-gray-600 font-montserrat">
                  MP4, WEBM, MOV, AVI
                </p>
              </div>
              <div className="p-6 bg-green-50 rounded-2xl">
                <FileAudio className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <h4 className="font-amoria font-bold text-gray-900 mb-2">Audio</h4>
                <p className="text-sm text-gray-600 font-montserrat">
                  MP3, WAV, OGG, AAC
                </p>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Tools;
